import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

type ExcludeRule = string | RegExp | ((_pathname: string) => boolean);

export const useScrollToTop = (options?: { exclude?: ExcludeRule[] }) => {
  const { pathname, state } = useLocation();
  const anchor = state?.anchor ?? '';
  const preserveScroll = state?.preserveScroll === true;
  const excludeRef = useRef<ExcludeRule[] | undefined>(options?.exclude);
  const leavingPreservedRef = useRef(false);

  useEffect(() => {
    excludeRef.current = options?.exclude;
  }, [options?.exclude]);

  useEffect(() => {
    const isExcluded = () => {
      if (!excludeRef.current?.length) return false;
      return excludeRef.current.some((rule) => {
        if (typeof rule === 'string') return pathname.startsWith(rule);
        if (rule instanceof RegExp) return rule.test(pathname);
        return rule(pathname);
      });
    };

    // A preserveScroll entry is an overlay (offer drawer): keep the page in place when it opens and
    // when navigation leaves it (close via history back).
    const keepScroll = preserveScroll || leavingPreservedRef.current;
    leavingPreservedRef.current = preserveScroll;

    if ((isExcluded() || keepScroll) && !anchor) return;

    window.scrollTo(0, 0);
    if (anchor) {
      const anchorElement = document.getElementById(anchor);
      anchorElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pathname, anchor, preserveScroll]);
};
